import { useState } from 'react';
import { Play, Plus, Lock, Volume2, Eye } from 'lucide-react';
import { AutomationLane } from './AutomationLane';
import { CommentTrack } from './CommentTrack';

interface PlaylistClip {
  id: string;
  pattern: number;
  track: number;
  start: number;
  length: number;
  color: string;
  name: string;
}

export function Playlist() {
  const [clips] = useState<PlaylistClip[]>([
    { id: '1', pattern: 1, track: 0, start: 0, length: 16, color: '#ff6b00', name: 'Drums' },
    { id: '2', pattern: 2, track: 1, start: 16, length: 16, color: '#00ff6b', name: 'Bass' },
    { id: '3', pattern: 1, track: 0, start: 32, length: 16, color: '#ff6b00', name: 'Drums' },
    { id: '4', pattern: 3, track: 2, start: 0, length: 32, color: '#6b00ff', name: 'Lead' },
    { id: '5', pattern: 4, track: 3, start: 16, length: 48, color: '#00b4ff', name: 'Pad' },
  ]);

  const [showAutomation, setShowAutomation] = useState(true);
  const bars = 64;
  const zoom = 1;

  const trackData = [
    { name: 'Drums', locked: false, muted: false, solo: false, volume: 80 },
    { name: 'Bass', locked: false, muted: false, solo: false, volume: 75 },
    { name: 'Lead', locked: false, muted: false, solo: false, volume: 70 },
    { name: 'Pad', locked: false, muted: false, solo: false, volume: 60 },
    { name: 'FX', locked: false, muted: false, solo: false, volume: 65 },
    { name: 'Vocals', locked: true, muted: false, solo: false, volume: 85 },
    { name: 'Percussion', locked: false, muted: false, solo: false, volume: 55 },
    { name: 'Synth', locked: false, muted: false, solo: false, volume: 70 },
  ];

  return (
    <div className="flex-1 bg-[#2a2a2a] border-t border-[#3a3a3a] overflow-hidden flex flex-col">
      {/* Toolbar */}
      <div className="h-8 bg-[#1f1f1f] flex items-center px-3 border-b border-[#3a3a3a] justify-between">
        <div className="flex items-center gap-3">
          <span className="text-xs">PLAYLIST / ARRANGEMENT</span>
          <button className="text-[10px] px-2 py-0.5 bg-[#3a3a3a] hover:bg-[#4a4a4a] rounded flex items-center gap-1">
            <Plus size={10} />
            Add Track
          </button>
          <button
            onClick={() => setShowAutomation(!showAutomation)}
            className={`text-[10px] px-2 py-0.5 rounded flex items-center gap-1 ${
              showAutomation ? 'bg-[#ff6b00]' : 'bg-[#3a3a3a] hover:bg-[#4a4a4a]'
            }`}
          >
            <Eye size={10} />
            Automation
          </button>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] opacity-50">Snap: 1/4</span>
          <span className="text-[10px] opacity-50">Zoom</span>
          <input type="range" min="0.5" max="2" step="0.1" defaultValue="1" className="w-16" />
        </div>
      </div>

      {/* Timeline and Tracks */}
      <div className="flex-1 flex overflow-hidden">
        {/* Track names */}
        <div className="w-40 bg-[#1f1f1f] border-r border-[#3a3a3a] flex flex-col">
          <div className="h-10 border-b border-[#3a3a3a] flex items-center justify-center text-[10px] opacity-50 bg-[#252525]">
            TRACKS
          </div>
          {trackData.map((track, i) => (
            <div
              key={i}
              className="h-20 border-b border-[#3a3a3a] px-2 py-2 hover:bg-[#252525] cursor-pointer flex flex-col gap-1"
            >
              <div className="flex items-center gap-1.5">
                <button className="p-0.5 hover:bg-[#2a2a2a] rounded">
                  <Play size={10} className="opacity-50" />
                </button>
                <span className="text-xs flex-1 truncate">{track.name}</span>
                {track.locked && <Lock size={10} className="text-yellow-500" />}
              </div>
              
              <div className="flex items-center gap-1">
                <button 
                  className={`text-[9px] px-1.5 py-0.5 rounded ${
                    track.muted ? 'bg-red-600' : 'bg-[#3a3a3a] hover:bg-[#4a4a4a]'
                  }`}
                >
                  M
                </button>
                <button 
                  className={`text-[9px] px-1.5 py-0.5 rounded ${
                    track.solo ? 'bg-green-600' : 'bg-[#3a3a3a] hover:bg-[#4a4a4a]'
                  }`}
                >
                  S
                </button>
                <div className="flex-1 flex items-center gap-1">
                  <Volume2 size={10} className="opacity-50" />
                  <div className="flex-1 h-1 bg-[#2a2a2a] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#ff6b00]"
                      style={{ width: `${track.volume}%` }}
                    />
                  </div>
                </div>
              </div>
              
              <div className="text-[9px] opacity-50">Track {i + 1}</div>
            </div>
          ))}
        </div>

        {/* Timeline Grid */}
        <div className="flex-1 overflow-auto">
          <div style={{ transform: `scale(${zoom})`, transformOrigin: 'top left' }}>
            {/* Time ruler */}
            <div className="h-10 bg-[#1f1f1f] border-b border-[#3a3a3a] flex sticky top-0 z-10">
              {Array.from({ length: bars / 4 }, (_, i) => (
                <div
                  key={i}
                  className="w-64 border-l border-[#4a4a4a] flex flex-col"
                >
                  <div className="flex-1 flex items-center justify-center text-[10px] opacity-50">
                    Bar {i * 4 + 1}
                  </div>
                  <div className="h-2 flex">
                    {[0, 1, 2, 3].map((beat) => (
                      <div
                        key={beat}
                        className="flex-1 border-l border-[#2a2a2a] text-[8px] text-center opacity-30"
                      >
                        {beat + 1}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Tracks */}
            <div className="relative">
              {/* Grid background */}
              {trackData.map((_, trackIndex) => (
                <div key={trackIndex} className="h-20 border-b border-[#3a3a3a] flex">
                  {Array.from({ length: bars }, (_, barIndex) => (
                    <div
                      key={barIndex}
                      className={`w-16 border-l ${
                        barIndex % 4 === 0 ? 'border-[#4a4a4a]' : 'border-[#2a2a2a]'
                      } hover:bg-[#303030] cursor-pointer transition-colors relative group`}
                    >
                      {barIndex % 4 === 0 && (
                        <div className="absolute inset-0 bg-[#ff6b00] opacity-0 group-hover:opacity-5 transition-opacity" />
                      )}
                    </div>
                  ))}
                </div>
              ))}

              {/* Clips */}
              <div className="absolute inset-0 pointer-events-none">
                {clips.map((clip) => (
                  <div
                    key={clip.id}
                    className="absolute rounded overflow-hidden pointer-events-auto cursor-move border-2 border-black/30 hover:border-white/30 transition-all group"
                    style={{
                      left: `${clip.start * 16}px`,
                      top: `${clip.track * 80 + 4}px`,
                      width: `${clip.length * 16}px`,
                      height: '72px',
                      backgroundColor: clip.color,
                    }}
                  >
                    <div className="p-2 relative h-full flex flex-col">
                      <div className="text-xs opacity-90 truncate">{clip.name}</div>
                      <div className="text-[10px] opacity-70 mb-1">
                        Pattern {clip.pattern}
                      </div>
                      
                      {/* Waveform visualization */}
                      <div className="flex-1 flex items-end gap-0.5">
                        {Array.from({ length: Math.floor(clip.length * 2) }, (_, i) => (
                          <div
                            key={i}
                            className="flex-1 bg-black/30 rounded-t"
                            style={{ height: `${20 + Math.random() * 80}%` }}
                          />
                        ))}
                      </div>
                    </div>
                    
                    {/* Resize handles */}
                    <div className="absolute left-0 top-0 bottom-0 w-2 bg-white/0 hover:bg-white/20 cursor-ew-resize opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="absolute right-0 top-0 bottom-0 w-2 bg-white/0 hover:bg-white/20 cursor-ew-resize opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                ))}
              </div>

              {/* Playhead */}
              <div className="absolute top-0 bottom-0 w-0.5 bg-[#ff6b00] shadow-lg shadow-[#ff6b00]/50 pointer-events-none" style={{ left: '128px' }}>
                <div className="w-4 h-4 -ml-2 bg-[#ff6b00] clip-path-triangle" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Comment Track - Requirements 3.1 */}
      <CommentTrack bars={bars} pixelsPerBeat={16} />

      {/* Automation lanes */}
      {showAutomation && (
        <div className="border-t-2 border-[#3a3a3a]">
          <AutomationLane parameter="Master Volume" color="#ff6b00" />
          <AutomationLane parameter="Filter Cutoff" color="#00ff6b" />
        </div>
      )}

      {/* Bottom info */}
      <div className="h-6 bg-[#1f1f1f] border-t border-[#3a3a3a] px-3 flex items-center justify-between text-[10px] opacity-70">
        <span>Drag patterns from browser • Right-click for options • Scroll wheel to zoom</span>
        <span>Clips: {clips.length} • Length: {bars} bars</span>
      </div>
    </div>
  );
}